import PdfRendererController from "./pdf_renderer_controller";

// Connects to data-controller="stipulation-pdf-viewer"
// Automatically loads and displays a PDF from a URL when the page loads
// Supports rendering attorney signing fields from tagging_data
export default class extends PdfRendererController {
  static targets = ["signButton", "submitButton", "canvas", "pagePreview"];
  static values = {
    url: String,
    filename: String,
    taggingData: Object,
    awaitingCountersignature: Boolean,
    canSign: Boolean,
  };

  connect() {
    super.connect();
    this.attorneyFields = [];
    this.currentFieldIndex = 0;
    this.isSigningMode = false;

    // Get reference to the form element
    this.formElement =
      this.element.tagName === "FORM" ? this.element : this.element.closest("form");

    // Automatically load the PDF when the controller connects
    if (this.hasUrlValue && this.hasFilenameValue) {
      this.load_pdf_from_url();
    }
  }

  async load_pdf_from_url() {
    try {
      const response = await fetch(this.urlValue);
      if (!response.ok) {
        throw new Error(`Failed to load PDF: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const pdf_bytes = new Uint8Array(arrayBuffer);

      // Mark this file as rendered to prevent duplicates
      this.renderedFiles.add(this.filenameValue);

      // Render the PDF
      await this.render_pdf(pdf_bytes, this.filenameValue);
    } catch (error) {
      console.error("Error loading PDF:", error);
      this.show_error_message(error.message);
    }
  }

  // Override hook to add attorney signing fields after each page is rendered
  async on_page_rendered(_page, canvas, viewport, page_num, pdf_wrapper, scale) {
    // Create an overlay for positioning input fields
    const overlay = this.create_overlay(canvas, viewport, page_num, pdf_wrapper, scale);
    pdf_wrapper.appendChild(overlay);

    // If we have tagging data, render attorney input fields
    if (this.hasTaggingDataValue) {
      this.render_attorney_fields(page_num, overlay);
    }
  }

  create_overlay(canvas, viewport, page_num, pdf_wrapper, scale) {
    const root_font_size = this.get_root_font_size();

    // Get actual canvas dimensions directly
    const canvas_rect = canvas.getBoundingClientRect();
    const wrapper_rect = pdf_wrapper.getBoundingClientRect();

    // Calculate relative position and dimensions
    const relative_top = canvas_rect.top - wrapper_rect.top;
    const relative_left = canvas_rect.left - wrapper_rect.left;
    const canvas_width = canvas_rect.width;
    const canvas_height = canvas_rect.height;

    const div_overlay = document.createElement("div");
    div_overlay.classList.add("pdf-input-overlay");
    div_overlay.dataset.pageNumber = page_num;
    div_overlay.dataset.viewportWidth = viewport.width;
    div_overlay.dataset.viewportHeight = viewport.height;
    div_overlay.dataset.pdfScale = scale;

    // Use the canvas dimensions directly for perfect alignment
    div_overlay.style.position = "absolute";
    div_overlay.style.width = `${canvas_width / root_font_size}rem`;
    div_overlay.style.height = `${canvas_height / root_font_size}rem`;
    div_overlay.style.top = `${relative_top / root_font_size}rem`;
    div_overlay.style.left = `${relative_left / root_font_size}rem`;
    div_overlay.style.pointerEvents = "none"; // Allow clicking through empty areas

    return div_overlay;
  }

  render_attorney_fields(page_num, overlay) {
    // Only render input fields if awaiting countersignature AND user has permission to sign
    if (!this.awaitingCountersignatureValue || !this.canSignValue) {
      return;
    }

    // Check if there's tagging data for this page
    const page_key = `page_${page_num}`;
    const fields = this.taggingDataValue[page_key];
    if (!fields || !Array.isArray(fields)) return;

    // Filter for attorney fields only
    const attorney_fields = fields.filter((field) => field.id && field.id.startsWith("attorney_"));

    attorney_fields.forEach((field) => {
      this.create_attorney_input_field(field, overlay);
    });
  }

  create_attorney_input_field(field, overlay) {
    const input_wrapper = document.createElement("div");
    input_wrapper.classList.add("attorney-input-field");
    input_wrapper.dataset.fieldId = field.id;
    input_wrapper.dataset.fieldType = field.type;

    // Position the field based on percentage values
    const pct = field.pct_values;
    input_wrapper.style.position = "absolute";
    input_wrapper.style.left = `${pct.xstart}%`;
    const offset = 0.75;
    input_wrapper.style.top = `${pct.ystart - offset}%`;
    input_wrapper.style.width = `${pct.width}%`;
    input_wrapper.style.height = `${pct.height}%`;
    input_wrapper.style.minHeight = "2.5rem"; // Ensure minimum usable height
    input_wrapper.style.pointerEvents = "auto"; // Re-enable pointer events for the input

    // Create the actual input element
    const input = this.create_input_element(field);
    input_wrapper.appendChild(input);

    overlay.appendChild(input_wrapper);
  }

  create_input_element(field) {
    let input;

    switch (field.type) {
      case "signature":
        input = document.createElement("input");
        input.type = "text";
        input.placeholder = "Type signature here";
        input.classList.add("signature-input");
        input.name = `stipulation[${field.id}]`;
        break;

      case "date":
        input = document.createElement("input");
        input.type = "date";
        input.name = `stipulation[${field.id}]`;
        input.classList.add("date-input");
        break;

      case "text":
      default:
        input = document.createElement("input");
        input.type = "text";
        input.placeholder = this.get_field_label(field.id);
        input.name = `stipulation[${field.id}]`;
        input.classList.add("text-input");
        break;
    }

    input.classList.add("attorney-field-input");

    // Add input event listener to track field completion
    input.addEventListener("input", () => this.check_field_completion());

    // Add Enter key listener to move to next field
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && this.isSigningMode) {
        e.preventDefault();
        this.move_to_next_field();
      }
    });

    // Store reference to this input field
    this.attorneyFields.push(input);

    return input;
  }

  get_field_label(field_id) {
    const labels = {
      attorney_signature: "Attorney Signature",
      attorney_name: "Attorney Name",
      attorney_date: "Date",
    };
    return labels[field_id] || field_id.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  }

  show_error_message(message) {
    if (this.hasCanvasTarget) {
      const error_div = document.createElement("div");
      error_div.classList.add("pdf-load-error");
      error_div.innerHTML = `
        <p class="error-message">Unable to load PDF: ${message}</p>
        <p>Please try refreshing the page or contact support if the problem persists.</p>
      `;
      this.canvasTarget.appendChild(error_div);
    }
  }

  // Signing workflow methods
  start_signing() {
    if (this.attorneyFields.length === 0) {
      alert("No attorney fields found to sign.");
      return;
    }

    this.isSigningMode = true;
    this.currentFieldIndex = 0;

    // Highlight all fields to show what needs to be filled
    this.attorneyFields.forEach((field) => {
      field.closest(".attorney-input-field").classList.add("signing-mode");
    });

    // Focus on the first field and scroll to it
    this.focus_on_current_field();

    // Hide sign button
    if (this.hasSignButtonTarget) {
      this.signButtonTarget.style.display = "none";
    }
  }

  focus_on_current_field() {
    if (this.currentFieldIndex >= this.attorneyFields.length) return;

    const current_field = this.attorneyFields[this.currentFieldIndex];
    const field_wrapper = current_field.closest(".attorney-input-field");

    // Remove active class from all fields
    this.attorneyFields.forEach((field) => {
      field.closest(".attorney-input-field").classList.remove("active");
    });

    // Add active class to current field
    field_wrapper.classList.add("active");

    // Scroll to the field
    field_wrapper.scrollIntoView({ behavior: "smooth", block: "center" });

    // Focus the input
    setTimeout(() => {
      current_field.focus();
    }, 500); // Wait for scroll to complete
  }

  move_to_next_field() {
    // Check if current field has a value
    const current_field = this.attorneyFields[this.currentFieldIndex];
    if (!current_field.value || current_field.value.trim() === "") {
      alert("Please fill out this field before continuing.");
      return;
    }

    // Move to next field
    this.currentFieldIndex++;

    if (this.currentFieldIndex < this.attorneyFields.length) {
      this.focus_on_current_field();
    } else {
      // All fields completed
      this.complete_signing();
    }
  }

  complete_signing() {
    this.isSigningMode = false;

    // Remove active class from all fields
    this.attorneyFields.forEach((field) => {
      field.closest(".attorney-input-field").classList.remove("active");
      field.closest(".attorney-input-field").classList.add("completed");
    });

    // Enable submit button
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.scrollIntoView({ behavior: "smooth", block: "center" });
      this.submitButtonTarget.focus();
    }
  }

  check_field_completion() {
    // Check if all fields have values
    const all_filled = this.attorneyFields.every(
      (field) => field.value && field.value.trim() !== "",
    );

    // Enable/disable submit button based on completion
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !all_filled;
    }
  }

  submit_signature(event) {
    // Validate all fields are filled before submitting
    const all_filled = this.attorneyFields.every(
      (field) => field.value && field.value.trim() !== "",
    );

    if (!all_filled) {
      if (event) event.preventDefault();
      alert("Please fill out all fields before submitting.");
      return false;
    }

    // Form will submit naturally - browser handles everything
    // Rails handles CSRF token automatically via form_with
    return true;
  }
}
